import { useCallback } from 'react';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';

export interface MessageTreeNode {
  id: string;
  label: string;
  clientId?: string;
  children?: MessageTreeNode[];
}

export function useMessageTreeApi(companyId: string | number, clientId: string | number) {
  // Define the async function
  const fetchTreeData = useCallback(async (): Promise<MessageTreeNode[]> => {
    if (!companyId && !clientId) return [];
    
    const response = await axiosInstance.get(`/Message/Tree`, {
      params: { 
        companyId: companyId ? String(companyId) : undefined,
        clientId: clientId ? String(clientId) : undefined
      }
    });
    return response.data || [];
  }, [companyId, clientId]);

  // Use useAsync hook with immediate execution
  const { value: tree, isLoading: loading, error, execute: refresh } = useAsync<MessageTreeNode[]>(
    fetchTreeData,
    true, // immediate = true (execute on mount)
    undefined, // no params needed (already in fetchTreeData closure)
    [] // initial state = empty array
  );

  // Convert error object to string for React rendering
  const errorMessage = error 
    ? (typeof error === 'string' ? error : (error as any)?.message || 'Failed to load tree')
    : null;

  return { tree, loading, error: errorMessage, refresh };
}
